import { z } from "zod";
import { Flight } from "@/core/types/flight.type";

// 🔑 Tie the Zod schema to your static type to guarantee they match perfectly
const airportObjectSchema = z.object({
  id: z.string(),
  code: z.string(),
  city: z.string(),
  region: z.string(),
  name: z.string(),
});

const flightSegmentSchema = z.object({
  type: z.literal("flight"),
  flight_type: z.union([
    z.literal(Flight.ONEWAY),
    z.literal(Flight.ROUNDTRIP),
  ]),
  departure_airport: airportObjectSchema.optional().refine((v) => v !== undefined, { message: "flightReq.errors.departure_airport_required" }),
  arrival_airport: airportObjectSchema.optional().refine((v) => v !== undefined, { message: "flightReq.errors.arrival_airport_required" }),
  departure_date: z.date(),
  return_date: z.date().optional(),
  passenger_count: z.number().min(1, "passengers.required").max(9, "passengers.invalid"),
}).superRefine((data, ctx) => {
  if (data.flight_type === Flight.ROUNDTRIP && !data.return_date) {
    ctx.addIssue({
      code: "custom",
      message: "flightReq.errors.return_date_required",
      path: ["return_date"],
    });
  }

  if (data.departure_airport?.id === data.arrival_airport?.id) {
    ctx.addIssue({
      code: "custom",
      message: "Arrival airport cannot match Departure airport",
      path: ["arrival_airport"],
    });
  }
});

const hotelSegmentSchema = z.object({
  type: z.literal("hotel"),
  // Add hotel specific fields here (e.g., checkIn, checkOut, roomCount)
});

const vesselSegmentSchema = z.object({
  type: z.literal("vessel"),
  // Add vessel specific fields here
});

const experienceSegmentSchema = z.object({
  type: z.literal("experience"),
  // Add experience specific fields here
});

const segmentSchema = z.discriminatedUnion("type", [
  flightSegmentSchema,
  hotelSegmentSchema,
  vesselSegmentSchema,
  experienceSegmentSchema,
]);

export const flightFormSchema = z.object({
  email: z.string().min(1, "email.required").pipe(z.email("email.invalid")),
  given_name: z.string().min(1, "name.given_name.required").max(60, "name.given_name.invalid"),
  family_name: z.string().min(1, "name.family_name.required").max(60, "name.family_name.invalid"),
  phone_number: z.string().min(1, "phone_number.required").max(20, "phone_number.invalid"),
  submitted_at: z.string(),
  segments: z.array(segmentSchema).min(1, "At least one segment is required"),
});

// 🔑 This type is now perfectly mirrored with your validation configuration mechanics
export type FlightFormData = z.infer<typeof flightFormSchema>;

export type TripFormPayload = Omit<FlightFormData, "segments"> & {
  segments: (z.infer<typeof flightSegmentSchema> | z.infer<typeof hotelSegmentSchema> | z.infer<typeof vesselSegmentSchema> | z.infer<typeof experienceSegmentSchema>)[];
};
